import type { RequestFn } from '../http';
import type { WithKianUsage } from '../types/core';

/** Was mit dem Ticket geschehen soll — KIANs Vorschlag. */
export type WorklistActionType =
  | 'ASK_CUSTOMER'
  | 'ASK_INTERNAL'
  | 'DO_WORK'
  | 'CLOSE'
  | 'WAIT';

/** Wohin ein Kommentar geht. */
export type DraftVisibility = 'PUBLIC' | 'INTERNAL';

/** Der jeweils andere Weg bei Altlasten. */
export interface WorklistAlternative {
  actionType: WorklistActionType;
  actionSummary: string;
  draftText: string | null;
  /** Derselbe Sachverhalt in der jeweils anderen Sichtbarkeit. */
  draftTextOther: string | null;
  /** Für welche Sichtbarkeit `draftText` formuliert ist. */
  draftVisibility?: DraftVisibility;
  estimatedMinutes: number | null;
  reasoning: string;
}

export interface WorklistSuggestion {
  ticketId: string;
  ticketNumber: string | null;
  subject: string | null;
  partyName: string | null;
  createdDate: string | null;
  ageDays: number;
  /** 1 (sofort) bis 5 (kann warten) */
  priority: number;
  actionType: WorklistActionType;
  actionSummary: string;
  /** Fertiger Textvorschlag — vor dem Ausführen bearbeitbar. */
  draftText: string | null;
  /** Derselbe Sachverhalt für die jeweils andere Sichtbarkeit (intern/öffentlich). */
  draftTextOther: string | null;
  /**
   * Für welche Sichtbarkeit `draftText` formuliert ist — und was vorausgewählt
   * wird. Beim Schließen von Altlasten ist das intern.
   */
  draftVisibility?: DraftVisibility;
  estimatedMinutes: number | null;
  reasoning: string;
  /** Wann bewertet wurde — bei wiederverwendeten Vorschlägen älter als der Lauf. */
  assessedAt?: string;
  /** true = aus dem Bestand, nicht neu bewertet. */
  reused?: boolean;
  /** true = Ticket hat sich seit der Bewertung geändert, Sperrfrist läuft noch. */
  stale?: boolean;
  /** Zweiter Weg bei alten Tickets — aufräumen oder ernsthaft aufgreifen. */
  alternative?: WorklistAlternative | null;
}

export interface WorklistResult {
  suggestions: WorklistSuggestion[];
  skipped: { ticketId: string; ticketNumber: string | null; reason: string }[];
  assessedAt: string;
  /** Wie viele Vorschläge aus dem Bestand kamen (keine Kosten). */
  reusedCount: number;
  /** Wie viele Tickets neu bewertet wurden. */
  freshCount: number;
  /** Was dieser Lauf tatsächlich gekostet hat (EUR); null ohne hinterlegten Preis. */
  costEur: number | null;
}

/** Ein wählbares Ticket in der Kandidatenliste. */
export interface WorklistCandidate {
  ticketId: string;
  ticketNumber: string | null;
  subject: string | null;
  partyName: string | null;
  createdDate: string | null;
  ageDays: number;
  hasAssessment: boolean;
  changedSinceAssessment: boolean;
  /** true = darf jetzt (neu) bewertet werden. */
  assessable: boolean;
  blockedReason: string | null;
  /** true = ohne Admin-Recht wäre dieses Ticket gesperrt. */
  wouldBeBlocked: boolean;
  assessedAt: string | null;
  /** Bereits vorliegender Vorschlag — sofort abarbeitbar, ohne neue Bewertung. */
  suggestion: WorklistSuggestion | null;
}

/** Ticketinhalt zum Nachlesen in der Arbeitsliste. */
export interface WorklistTicketContext {
  ticket: {
    id: string;
    ticketNumber: string | null;
    subject: string | null;
    description: string | null;
    partyName: string | null;
    createdDate: string | null;
    dueDate: string | null;
    senderEmail: string | null;
  };
  comments: {
    id: string;
    comment: string;
    internal: boolean;
    createdDate: string | null;
    author: string | null;
  }[];
}

export function createKianWorklistApi(request: RequestFn) {
  return {
    /** Ticketinhalt und Kommentare — aus dem lokalen Spiegel, kostet nichts. */
    async getKianWorklistContext(ticketId: string) {
      return request<WorklistTicketContext>(
        `/kian/worklist/context/${encodeURIComponent(ticketId)}`
      );
    },

    /** Was zur Bewertung ansteht — kostet nichts, reine Datenbankabfrage. */
    async getKianWorklistCandidates(onlyMine = true, force = false) {
      return request<{
        candidates: WorklistCandidate[];
        canForce: boolean;
        /** Durchschnittskosten eines Bewertungsaufrufs aus den letzten 30 Tagen. */
        costPerCallEur: number | null;
        /** Wie viele Tickets ein Aufruf abdeckt — für die Hochrechnung. */
        ticketsPerCall: number | null;
      }>(`/kian/worklist/candidates?onlyMine=${onlyMine}&force=${force}`);
    },

    /** Offene Tickets von KIAN bewerten lassen (kostet LLM-Aufrufe). */
    async assessKianWorklist(
      body: { onlyMine?: boolean; limit?: number; ticketIds?: string[]; force?: boolean } = {}
    ) {
      return request<WithKianUsage<WorklistResult>>('/kian/worklist/assess', {
        method: 'POST',
        kianUsageInline: true,
        body: JSON.stringify(body),
      });
    },

    /**
     * Einen Entwurf in die andere Sichtbarkeit umschreiben lassen (ein kurzer
     * LLM-Aufruf). Nötig bei Bewertungen, die noch ohne zweite Fassung entstanden.
     */
    async rewriteKianWorklistDraft(body: {
      ticketId?: string;
      text: string;
      targetVisibility: DraftVisibility;
      /** Freie Anweisung, was anders werden soll. */
      instruction?: string;
    }) {
      return request<WithKianUsage<{ text: string; costEur: number | null }>>('/kian/worklist/rewrite', {
        method: 'POST',
        kianUsageInline: true,
        body: JSON.stringify(body),
      });
    },

    /** Vorschlag ausführen — `text` ist der ggf. angepasste Text. */
    async applyKianWorklistAction(body: {
      ticketId: string;
      actionType: WorklistActionType;
      text?: string;
      minutes?: number;
      /** Überstimmt die Voreinstellung. */
      visibility?: DraftVisibility;
    }) {
      return request<{ ok: boolean; applied: string }>('/kian/worklist/apply', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
  };
}
